import { ref, computed, watch, onMounted, onUnmounted, readonly } from 'vue'
import { useMercure } from './useMercure'
import { useAuthSession } from '@/modules/auth/public'
import { realtimeApi } from '@/modules/common/api/realtime.api'
import { calcReconnectDelay } from '@/modules/common/services/websocket/backoff'
import type { MercureConfig, ConnectionStatus } from '@/modules/common/services/websocket/types'

// Один общий коннект: повторный вызов initialize() ждёт завершения текущего, не создаёт второй
let initPromise: Promise<void> | null = null

// В dev-среде backend может вернуть localhost/labactive.local, а Vite открыт на 127.0.0.1.
// Браузер считает их разными origins → CORS. Нормализуем локальные dev hosts к текущему origin.
const LOCAL_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  'labactive.local',
  'api.labactive.local',
])

function isLocalDevHost(hostname: string): boolean {
  return LOCAL_HOSTNAMES.has(hostname.replace(/^\[|\]$/g, ''))
}

function normalizeMercureUrl(rawUrl: string): string {
  if (typeof window === 'undefined') {
    return rawUrl
  }
  try {
    const parsed = new URL(rawUrl)
    if (isLocalDevHost(parsed.hostname) && isLocalDevHost(window.location.hostname)) {
      parsed.protocol = window.location.protocol
      parsed.host = window.location.host
      return parsed.toString()
    }
  } catch {
    // malformed URL - вернём как есть
  }
  return rawUrl
}

export function useMercureConnection() {
  const {
    connect,
    disconnect,
    reconnect,
    updateToken,
    isConnected,
    connectionStatus,
    connectionError,
    metrics,
  } = useMercure()

  const auth = useAuthSession()
  const isInitialized = ref(false)
  const autoReconnect = ref(true)
  const connectionAttempts = ref(0)
  const maxConnectionAttempts = 5

  // Computed properties
  const canConnect = computed(() => {
    return !!auth.token.value && !!auth.user.value
  })

  const shouldAutoReconnect = computed(() => {
    return (
      autoReconnect.value && canConnect.value && connectionAttempts.value < maxConnectionAttempts
    )
  })

  const connectionStatusText = computed(() => {
    const statusMap: Record<ConnectionStatus, string> = {
      disconnected: 'Отключено',
      connecting: 'Подключение...',
      connected: 'Подключено',
      reconnecting: 'Переподключение...',
      error: 'Ошибка подключения',
    }

    return statusMap[connectionStatus.value] || 'Неизвестно'
  })

  // Create Mercure configuration
  const createConfig = async (): Promise<MercureConfig> => {
    if (!auth.user.value) {
      throw new Error('User not authenticated')
    }

    try {
      // Get JWT token from backend
      const tokenResponse = await realtimeApi.getMercureToken()

      if (!tokenResponse.success || !tokenResponse.data) {
        throw new Error(tokenResponse.error || 'Failed to get Mercure token')
      }

      return {
        hubUrl: normalizeMercureUrl(tokenResponse.data.mercure_url),
        jwtToken: tokenResponse.data.token,
        reconnectAttempts: 5,
        reconnectDelay: 1000,
        heartbeatInterval: 60000, // Увеличено до 60 секунд
        heartbeatTimeout: 90000, // Таймаут для heartbeat
        topics: tokenResponse.data.topics,
      }
    } catch (error) {
      console.error('Failed to create Mercure config:', error)
      throw error
    }
  }

  // Initialize connection (один раз глобально: повторные вызовы ждут ту же попытку)
  const initialize = async (): Promise<void> => {
    if (!canConnect.value) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      if (isInitialized.value || connectionStatus.value === 'connected') {
        isInitialized.value = true
        return
      }

      try {
        const config = await createConfig()
        await connect(config)

        isInitialized.value = true
        connectionAttempts.value = 0
        console.log('Mercure connection initialized successfully')
      } catch (error) {
        connectionAttempts.value++
        console.error('Failed to initialize Mercure connection:', error)

        if (shouldAutoReconnect.value) {
          const delay = calcReconnectDelay(connectionAttempts.value, 1000)
          console.log(
            `Retrying in ${delay}ms (attempt ${connectionAttempts.value}/${maxConnectionAttempts})`,
          )
          setTimeout(() => {
            initPromise = null
            initialize().catch(console.error)
          }, delay)
        }
        throw error
      } finally {
        initPromise = null
      }
    })()

    return initPromise
  }

  // Cleanup connection
  const cleanup = (): void => {
    disconnect()
    isInitialized.value = false
    connectionAttempts.value = 0
  }

  // Manual reconnection
  const manualReconnect = async (): Promise<void> => {
    connectionAttempts.value = 0

    if (isInitialized.value) {
      await reconnect()
    } else {
      await initialize()
    }
  }

  // Handle token refresh
  const handleTokenRefresh = (newToken: string): void => {
    if (isInitialized.value) {
      updateToken(newToken)
    }
  }

  // Enable/disable auto-reconnect
  const setAutoReconnect = (enabled: boolean): void => {
    autoReconnect.value = enabled
  }

  // Get connection info
  const getConnectionInfo = () => ({
    isConnected: isConnected.value,
    status: connectionStatus.value,
    statusText: connectionStatusText.value,
    error: connectionError.value,
    attempts: connectionAttempts.value,
    maxAttempts: maxConnectionAttempts,
    autoReconnect: autoReconnect.value,
    canConnect: canConnect.value,
    metrics: metrics.value,
  })

  // Watch for auth changes (via the public auth session contract, not the
  // internal Pinia store - this composable only knows about `canConnect`).
  const watchAuthChanges = (canConnectNow: boolean) => {
    // Initialize when user logs in
    if (canConnectNow && !isInitialized.value) {
      initialize().catch(console.error)
    }

    // Cleanup when user logs out
    if (!canConnectNow && isInitialized.value) {
      cleanup()
    }
  }

  onMounted(() => {
    watch(canConnect, (value) => watchAuthChanges(value))

    // Initialize if already authenticated
    if (canConnect.value) {
      initialize().catch(console.error)
    }
  })

  onUnmounted(() => {
    // Don't cleanup global connection on component unmount
    // It should persist across components
  })

  return {
    // State
    isConnected: readonly(isConnected),
    connectionStatus: readonly(connectionStatus),
    connectionStatusText: readonly(connectionStatusText),
    connectionError: readonly(connectionError),
    isInitialized: readonly(isInitialized),
    autoReconnect: readonly(autoReconnect),
    connectionAttempts: readonly(connectionAttempts),
    canConnect: readonly(canConnect),
    metrics: readonly(metrics),

    // Methods
    initialize,
    cleanup,
    manualReconnect,
    handleTokenRefresh,
    setAutoReconnect,
    getConnectionInfo,
  }
}
